import {
  FormEvent,
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import Markdown from 'markdown-to-jsx';
import styled, { css, keyframes } from 'styled-components';

import { getUserId } from '@paradigm/store/src/auth/selectors';

import {
  clearAgentSession,
  deriveDefaultAgentUrl,
  getAgentToken,
  getAgentUrl,
  getDeviceToken,
  isValidAgentUrl,
  setAgentToken,
} from '#/api/terminal/agent-url';
import { ChatMessage } from '#/api/terminal/types';
import {
  useAgentActions,
  useAgentState,
} from '#/features/terminal/agent-context';

import ApprovalCard from './ApprovalCard';

export default function ChatView() {
  const agentState = useAgentState();
  const userId = useSelector(getUserId);
  // Set when the user clicks "Disconnect"; surfaces the setup form again
  // (pre-filled with the previously-used URL + token) so reconnecting is a
  // one-click action rather than a re-entry. Cleared when the form is
  // submitted, at which point ChatInterface remounts.
  const [manualDisconnect, setManualDisconnect] = useState(false);

  if (userId == null) {
    return <SignedOutState />;
  }

  const userIdStr = String(userId);

  const showSetupForm =
    agentState.agentUrl == null ||
    agentState.agentUrl.length === 0 ||
    manualDisconnect;

  if (showSetupForm) {
    return (
      <AgentUrlSetup
        userId={userIdStr}
        onConnected={() => setManualDisconnect(false)}
      />
    );
  }

  return (
    <ChatInterface
      userId={userIdStr}
      onManualDisconnect={() => setManualDisconnect(true)}
    />
  );
}

function SignedOutState() {
  return (
    <Container>
      <EmptyState>
        <Title>Sign in to use the block terminal</Title>
        <Description>
          The block terminal pairs an AI agent with your paradigm account so you
          can ask about block trades flowing through the tape.
        </Description>
      </EmptyState>
    </Container>
  );
}

function AgentUrlSetup({
  userId,
  onConnected,
}: {
  readonly userId: string;
  readonly onConnected?: () => void;
}) {
  const user = useSelector((state) => state.auth.user);
  const deskTicker = user?.trading_desk?.ticker;
  const numericUserId = parseInt(userId, 10);
  const defaultUrl =
    deskTicker != null && !Number.isNaN(numericUserId)
      ? deriveDefaultAgentUrl(deskTicker, numericUserId)
      : '';
  // Pre-fill from localStorage so a manual disconnect → reconnect is a single
  // click rather than re-typing the URL/token. First-time users fall back to
  // the desk-derived default URL so they have a sensible starting point.
  const [url, setUrl] = useState(() => getAgentUrl(userId) ?? defaultUrl);
  const [token, setToken] = useState(() => getAgentToken(userId) ?? '');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const agentActions = useAgentActions();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmedUrl = url.trim();
    if (trimmedUrl.length === 0) return;
    if (!isValidAgentUrl(trimmedUrl)) return;
    // Only clear the existing device-token session when the URL is changing —
    // a stale token from a prior OpenClaw instance must not get replayed
    // against a different one. On same-URL reconnects (the common disconnect
    // → connect flow) keep the token so pairing isn't required again.
    const previousUrl = getAgentUrl(userId);
    if (previousUrl !== trimmedUrl) {
      clearAgentSession(userId);
    }
    if (token.trim().length > 0) {
      setAgentToken(userId, token.trim());
    }
    agentActions.setUrl(trimmedUrl);
    agentActions.connect(trimmedUrl);
    onConnected?.();
  };

  const isUrlValid = url.trim().length === 0 || isValidAgentUrl(url.trim());

  return (
    <Container>
      <EmptyState>
        <Title>Connect your AI agent</Title>
        <Description>
          Enter the OpenClaw gateway URL. After it connects, pair this device
          from the terminal tab to activate block-trade analysis.
        </Description>
        <SetupForm onSubmit={handleSubmit}>
          <UrlInput
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={
              defaultUrl.length > 0 ? defaultUrl : 'wss://your-agent-host'
            }
            autoComplete="off"
            $invalid={!isUrlValid}
          />
          <AdvancedToggle
            type="button"
            onClick={() => setShowAdvanced((visible) => !visible)}
          >
            {showAdvanced ? 'Hide advanced' : 'Advanced'}
          </AdvancedToggle>
          {showAdvanced ? (
            <UrlInput
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Gateway token"
              autoComplete="off"
            />
          ) : null}
          <ConnectButton
            type="submit"
            disabled={url.trim().length === 0 || !isUrlValid}
          >
            Connect gateway
          </ConnectButton>
        </SetupForm>
      </EmptyState>
    </Container>
  );
}

function ChatInterface({
  userId,
  onManualDisconnect,
}: {
  readonly userId: string;
  readonly onManualDisconnect?: () => void;
}) {
  const state = useAgentState();
  const actions = useAgentActions();
  const [input, setInput] = useState('');
  const [pairingState, setPairingState] = useState<
    'idle' | 'pending' | 'paired' | 'error'
  >('idle');
  const [pairingError, setPairingError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /**
   * Whether the user is currently pinned at the bottom of the messages
   * list. Updated on every scroll. We only auto-scroll when this is
   * true so that scrolling up to read earlier content doesn't get
   * yanked back to the bottom on the next streaming delta.
   */
  const stickToBottomRef = useRef(true);

  // Auto-connect on mount if a URL is configured but we're not yet
  // connected. Mount-only by design — depending on `state` would cause
  // reconnect loops on every status change.
  useEffect(() => {
    if (state.agentUrl != null && state.status === 'disconnected') {
      actions.connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (el == null) return;
    // 24px slack so a tiny gap from a smooth-scroll animation still
    // counts as "at the bottom".
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  }, []);

  useLayoutEffect(() => {
    const el = messagesContainerRef.current;
    if (el == null) return;
    if (!stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [state.messages, state.processing]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (trimmed.length === 0) return;
    if (needsPairing(userId, pairingState, state.status)) return;
    actions.sendMessage(trimmed);
    setInput('');
    inputRef.current?.focus();
  }, [input, actions, userId, pairingState, state.status]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDisconnect = () => {
    // Close the WS and surface the setup form. The persisted URL/token are
    // left intact so the form re-mounts pre-filled — clicking Connect again
    // is a single-action reconnect.
    actions.disconnect();
    onManualDisconnect?.();
  };

  const handleReconnect = () => {
    actions.connect();
  };

  const handlePairDevice = async () => {
    setPairingState('pending');
    setPairingError(null);
    try {
      await actions.requestPairing();
      setPairingState('paired');
      actions.connect();
    } catch (err) {
      setPairingState('error');
      setPairingError(err instanceof Error ? err.message : 'Pairing failed');
    }
  };

  const shouldPair = needsPairing(userId, pairingState, state.status);

  return (
    <Container>
      <StatusBar>
        <StatusDot $status={state.status} />
        <StatusText>{statusLabel(state)}</StatusText>
        <AgentUrlText title={state.agentUrl ?? ''}>
          {state.agentUrl}
        </AgentUrlText>
        <StatusActions>
          {state.status === 'disconnected' || state.status === 'error' ? (
            <SmallButton type="button" onClick={handleReconnect}>
              Reconnect
            </SmallButton>
          ) : null}
          <SmallButton type="button" onClick={() => actions.clearMessages()}>
            Clear
          </SmallButton>
          <SmallButton type="button" onClick={handleDisconnect}>
            Disconnect
          </SmallButton>
        </StatusActions>
      </StatusBar>

      {shouldPair ? (
        <PairingBanner>
          <PairingCopy>
            <strong>Pair this device</strong>
            <span>
              Approve the request on the OpenClaw host, then the terminal will
              reconnect as an operator.
            </span>
            {pairingError != null ? (
              <PairingError>{pairingError}</PairingError>
            ) : null}
          </PairingCopy>
          <ConnectButton
            type="button"
            onClick={handlePairDevice}
            disabled={pairingState === 'pending'}
          >
            {pairingState === 'pending' ? 'Pairing…' : 'Pair this device'}
          </ConnectButton>
        </PairingBanner>
      ) : null}

      <Messages
        ref={messagesContainerRef}
        onScroll={handleScroll}
        data-testid="terminal-messages"
      >
        {state.messages.length === 0 && state.processing == null ? (
          <Hint>
            Ask a question — e.g.{' '}
            <em>“What&rsquo;s flowing through the tape?”</em> or{' '}
            <em>“Summarize the last hour of BTC block trades”</em>.
          </Hint>
        ) : null}
        {state.messages.map((m) => (
          <MessageRow
            key={m.id}
            message={m}
            onRespondApproval={actions.respondToApproval}
          />
        ))}
        {state.processing != null ? (
          <ProcessingRow>
            <Dots />
            <span>
              {state.processing.label}
              {state.processing.detail != null
                ? ` — ${state.processing.detail}`
                : ''}
            </span>
          </ProcessingRow>
        ) : null}
      </Messages>

      <InputRow>
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            shouldPair
              ? 'Pair this device to start chatting...'
              : 'Ask about block trades...'
          }
          rows={1}
          disabled={shouldPair}
        />
        <SendButton
          type="button"
          onClick={handleSend}
          disabled={shouldPair || input.trim().length === 0}
        >
          Send
        </SendButton>
      </InputRow>
    </Container>
  );
}

function needsPairing(
  userId: string,
  pairingState: 'idle' | 'pending' | 'paired' | 'error',
  status: ReturnType<typeof useAgentState>['status'],
): boolean {
  if (pairingState === 'paired') return false;
  if (status !== 'authenticated') return false;
  return getAgentToken(userId) == null && getDeviceToken(userId) == null;
}

function statusLabel(state: ReturnType<typeof useAgentState>): string {
  switch (state.status) {
    case 'authenticated':
    case 'connected':
      return 'Connected';
    case 'connecting':
      return 'Connecting…';
    case 'authenticating':
      return 'Authenticating…';
    case 'error':
      return state.errorMessage ?? 'Connection error';
    default:
      return 'Disconnected';
  }
}

const MessageRow = memo(function MessageRow({
  message,
  onRespondApproval,
}: {
  readonly message: ChatMessage;
  readonly onRespondApproval: (approvalId: string, approved: boolean) => void;
}) {
  if (message.approval != null) {
    return (
      <Bubble $role="assistant">
        <ApprovalCard
          approval={message.approval}
          onRespond={onRespondApproval}
        />
      </Bubble>
    );
  }
  return (
    <Bubble $role={message.role}>
      <Markdown options={MARKDOWN_OPTIONS}>{message.content}</Markdown>
      {message.isStreaming === true ? <StreamingDot /> : null}
    </Bubble>
  );
});

const MARKDOWN_OPTIONS = {
  forceBlock: false,
  overrides: {
    a: {
      props: { target: '_blank', rel: 'noreferrer noopener' },
    },
  },
};

// --- styling ---

const Container = styled.div`
  display: flex;
  flex-direction: column;
  /* flex: 1 (not height: 100%) — height: 100% inside a flex parent
     evaluates against the parent's resolved height and can be flaky
     while the layout is still being computed. flex: 1 + min-height: 0
     reliably claims the parent's remaining space and lets the Messages
     list scroll inside. */
  flex: 1;
  min-height: 0;
  width: 100%;
  background: var(--bg-color-container, #0e131a);
  color: var(--text-primary, #fff);
  font-size: 12px;
`;

const EmptyState = styled.div`
  margin: auto;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 480px;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
`;

const Description = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 20px;
  line-height: 1.5;
`;

const SetupForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const UrlInput = styled.input<{ $invalid?: boolean }>`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ $invalid }) =>
      $invalid === true
        ? 'var(--error-color, #f44336)'
        : 'var(--border-color)'};
  background: var(--bg-color-container);
  color: var(--text-primary);
  font-size: 12px;
  font-family: var(--font-mono, monospace);

  &::placeholder {
    color: var(--text-secondary);
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    border-color: var(--accent-color, #4caf50);
  }
`;

const ConnectButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  background: var(--accent-color, #4caf50);
  color: var(--cta-text-color, #fff);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AdvancedToggle = styled.button`
  align-self: center;
  border: none;
  background: none;
  color: var(--text-secondary);
  font-size: 11px;
  cursor: pointer;
  padding: 2px 0;

  &:hover {
    color: var(--text-primary);
  }
`;

const PairingBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--divider-color, #1f2937);
  background: rgba(255, 255, 255, 0.04);
`;

const PairingCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: var(--text-secondary);
  line-height: 1.4;

  strong {
    color: var(--text-primary);
    font-size: 12px;
  }
`;

const PairingError = styled.span`
  color: var(--error-color, #f44336);
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--divider-color, #1f2937);
  font-size: 11px;
  color: var(--text-secondary);
`;

const StatusDot = styled.span<{ $status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $status }) =>
    $status === 'authenticated' || $status === 'connected'
      ? 'var(--accent-color, #4caf50)'
      : $status === 'connecting' || $status === 'authenticating'
      ? '#ff9800'
      : $status === 'error'
      ? 'var(--error-color, #f44336)'
      : 'var(--text-secondary)'};
`;

const StatusText = styled.span`
  font-weight: 500;
  color: var(--text-primary);
`;

const AgentUrlText = styled.span`
  flex: 1;
  font-family: var(--font-mono, monospace);
  font-size: 10px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const SmallButton = styled.button`
  padding: 3px 10px;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background: none;
  color: var(--text-secondary);
  font-size: 11px;
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
    border-color: var(--text-primary);
  }
`;

const Messages = styled.div`
  flex: 1;
  /* min-height: 0 lets overflow-y engage in a flex column. Without it
     the messages list grows to fit its content and shoves the input
     row off-screen on long agent replies. */
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Hint = styled.div`
  margin: auto;
  color: var(--text-secondary);
  font-size: 12px;
  text-align: center;
  max-width: 360px;
  line-height: 1.5;
`;

const Bubble = styled.div<{ $role: ChatMessage['role'] }>`
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 8px;
  word-break: break-word;
  ${({ $role }) =>
    $role === 'user'
      ? css`
          align-self: flex-end;
          background: var(--accent-color, #4caf50);
          color: var(--cta-text-color, #fff);
        `
      : css`
          align-self: flex-start;
          background: rgba(255, 255, 255, 0.05);
          color: var(--text-primary);
        `}

  p {
    margin: 0;
  }
  p + p {
    margin-top: 6px;
  }
  pre {
    background: rgba(0, 0, 0, 0.3);
    padding: 6px 8px;
    border-radius: 4px;
    /* Cap code-block height with its own internal scroll. Without this
       a large JSON paste (e.g. "Analyze in Terminal" trade snapshots)
       inflates the bubble to ~80% of the panel height and pushes the
       agent's reply out of the visible area. */
    max-height: 240px;
    overflow: auto;
    margin: 0;
  }
  code {
    font-family: var(--font-mono, monospace);
    font-size: 11px;
  }
`;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

const StreamingDot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  margin-left: 4px;
  animation: ${blink} 1.2s infinite;
`;

const ProcessingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 11px;
  padding: 4px 8px;
`;

function Dots() {
  return (
    <DotsContainer>
      <Dot />
      <Dot />
      <Dot />
    </DotsContainer>
  );
}

const DotsContainer = styled.span`
  display: inline-flex;
  gap: 3px;
`;

const Dot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  animation: ${blink} 1.2s infinite;
  &:nth-child(2) {
    animation-delay: 0.15s;
  }
  &:nth-child(3) {
    animation-delay: 0.3s;
  }
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid var(--divider-color, #1f2937);
`;

const Input = styled.textarea`
  flex: 1;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: var(--bg-color-container);
  color: var(--text-primary);
  font-size: 12px;
  font-family: inherit;
  resize: none;

  &:focus {
    outline: none;
    border-color: var(--accent-color, #4caf50);
  }
`;

const SendButton = styled.button`
  padding: 6px 16px;
  border-radius: 6px;
  border: none;
  background: var(--accent-color, #4caf50);
  color: var(--cta-text-color, #fff);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
